import type { CSSProperties } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { api } from '@/shared/api';
import { NAV_ITEMS } from './config';
import styles from './BottomNav.module.less';

/** Больше не помещается в кружок — дальше показываем «9+» */
const MAX_BADGE = 9;

/**
 * Путь принадлежит разделу, если равен его корню или лежит внутри.
 * Через `/` в конце, а не голым startsWith: иначе «/cart» подсвечивал бы
 * «Каталог», а «/catalog» — «Корзину».
 */
function isSection(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function BottomNav() {
  const location = useLocation();
  const fromPath = (location.state as { from?: string } | null)?.from;

  // Индекс раздела для светового пятна. Считаем той же логикой, по которой
  // NavLink красит иконку белым, чтобы пятно и цвет не разъезжались.
  const currentIndex = NAV_ITEMS.findIndex(({ path }) =>
    isSection(location.pathname, path)
  );
  const fallbackIndex = fromPath
    ? NAV_ITEMS.findIndex(({ path }) => isSection(fromPath, path))
    : -1;
  const activeIndex = currentIndex >= 0 ? currentIndex : fallbackIndex;

  // На иконке корзины — число самих корзин, то есть будущих заказов,
  // а не суммарное количество товаров в них. Навигация висит на всех
  // экранах, поэтому берём отдельный лёгкий счётчик, а не список корзин:
  // его маппер ходит в catalog-service по разу на каждую корзину
  const { data: cartsCountData } = api.useGetCartsCountQuery();
  const cartsCount = cartsCountData?.count ?? 0;

  return (
    <div className={styles.area}>
      <nav className={styles.bar}>
        <span
          className={`${styles.glow} ${
            activeIndex < 0 ? styles.glowHidden : ''
          }`}
          style={
            { '--nav-active': Math.max(activeIndex, 0) } as CSSProperties
          }
          aria-hidden="true"
        />

        {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
          const isActiveByFrom = fromPath?.startsWith(path) ?? false;
          const badge = path === '/cart' ? cartsCount : 0;

          return (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              // Бейдж-кружок скринридеру не виден, поэтому счётчик
              // проговариваем в подписи самой ссылки
              aria-label={badge > 0 ? `${label}, ${badge}` : undefined}
              className={({ isActive }) =>
                [styles.item, isActive || isActiveByFrom ? styles.active : '']
                  .filter(Boolean)
                  .join(' ')
              }
            >
              <span className={styles.iconWrap}>
                <Icon />
                {badge > 0 && (
                  <span className={styles.badge} aria-hidden="true">
                    {badge > MAX_BADGE ? `${MAX_BADGE}+` : badge}
                  </span>
                )}
              </span>
              <span className={styles.label}>{label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
